/**
 * Coach busy intervals from existing academy lessons (group / camp / private) for private-booking UI and validation.
 */

import type { Lesson, Term } from '../types';
import { doTimesOverlap } from './timeOverlap';
import { generatePrivateLessonOccurrenceDates } from './lessonDates';

export function normalizeTimeHHMM(t: string): string {
  if (!t) return '00:00';
  return t.slice(0, 5);
}

export function toYmd(value: string | Date | undefined | null): string | null {
  if (value == null) return null;
  if (typeof value === 'string') return value.split('T')[0];
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function addDaysYmd(ymd: string, deltaDays: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + deltaDays);
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

function minYmd(a: string, b: string): string {
  return a <= b ? a : b;
}

function maxYmd(a: string, b: string): string {
  return a >= b ? a : b;
}

/** Calendar weekday (0–6) for a local YYYY-MM-DD. */
export function calendarDayOfWeekFromYmd(ymd: string): number {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d).getDay();
}

/**
 * Normalized days-of-week for a lesson (supports legacy single dayOfWeek and private startDate anchor).
 */
export function getLessonDaysOfWeek(lesson: Pick<Lesson, 'daysOfWeek' | 'startDate'> & { dayOfWeek?: number }): number[] {
  if (Array.isArray(lesson.daysOfWeek) && lesson.daysOfWeek.length > 0) {
    return lesson.daysOfWeek;
  }
  if (typeof lesson.dayOfWeek === 'number') {
    return [lesson.dayOfWeek];
  }
  const sd = lesson.startDate;
  if (sd != null) {
    const d = sd instanceof Date ? sd : new Date(sd);
    if (!Number.isNaN(d.getTime())) {
      return [d.getDay()];
    }
  }
  return [0];
}

function formatYmdLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * True if `weekday` occurs at least once in [rangeStart, rangeEnd] (inclusive, YYYY-MM-DD).
 */
export function weekdayOccursInYmdRange(weekday: number, rangeStart: string, rangeEnd: string): boolean {
  if (rangeStart > rangeEnd) return false;
  const cur = new Date(
    Number(rangeStart.slice(0, 4)),
    Number(rangeStart.slice(5, 7)) - 1,
    Number(rangeStart.slice(8, 10))
  );
  cur.setHours(0, 0, 0, 0);
  const end = new Date(
    Number(rangeEnd.slice(0, 4)),
    Number(rangeEnd.slice(5, 7)) - 1,
    Number(rangeEnd.slice(8, 10))
  );
  end.setHours(0, 0, 0, 0);
  for (let guard = 0; guard < 8 && cur <= end; guard++) {
    if (cur.getDay() === weekday) {
      return true;
    }
    cur.setDate(cur.getDate() + 1);
  }
  return false;
}

export interface WeeklyLessonOccupiedBlock {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
}

export interface CoachLessonProjectionOptions {
  startFromDate: string;
  /** Default 365 — must cover generated private session dates from startFromDate. */
  projectionDays?: number;
}

/**
 * Recurring weekly time blocks where the coach is assigned to another lesson that still has
 * sessions in [startFromDate, startFromDate + projectionDays].
 */
export function computeCoachWeeklyLessonOccupiedBlocks(
  lessons: Lesson[],
  termsById: Map<string, Pick<Term, 'startDate' | 'endDate'>>,
  options: CoachLessonProjectionOptions
): WeeklyLessonOccupiedBlock[] {
  const projectionDays = options.projectionDays ?? 365;
  const startFromDate = options.startFromDate;
  const projectionEnd = addDaysYmd(startFromDate, projectionDays);
  const out: WeeklyLessonOccupiedBlock[] = [];

  for (const lesson of lessons) {
    const st = normalizeTimeHHMM(lesson.startTime);
    const et = normalizeTimeHHMM(lesson.endTime);

    if (lesson.lessonType === 'group') {
      if (!lesson.termId) continue;
      const term = termsById.get(lesson.termId);
      if (!term) continue;
      const tStart = toYmd(term.startDate);
      const tEnd = toYmd(term.endDate);
      if (!tStart || !tEnd) continue;
      let effectiveEnd = tEnd;
      if (lesson.cancelledFromDate) {
        effectiveEnd = minYmd(effectiveEnd, addDaysYmd(lesson.cancelledFromDate, -1));
      }
      const rangeStart = maxYmd(tStart, startFromDate);
      const rangeEnd = minYmd(effectiveEnd, projectionEnd);
      if (rangeStart > rangeEnd) continue;
      const days = getLessonDaysOfWeek(lesson);
      if (days.length === 0) continue;
      for (const day of days) {
        if (weekdayOccursInYmdRange(day, rangeStart, rangeEnd)) {
          out.push({ dayOfWeek: day, startTime: st, endTime: et });
        }
      }
      continue;
    }

    if (lesson.lessonType === 'camp') {
      const cStart = toYmd(lesson.startDate);
      const cEnd = toYmd(lesson.endDate);
      if (!cStart || !cEnd) continue;
      let effectiveEnd = cEnd;
      if (lesson.cancelledFromDate) {
        effectiveEnd = minYmd(effectiveEnd, addDaysYmd(lesson.cancelledFromDate, -1));
      }
      const rangeStart = maxYmd(cStart, startFromDate);
      const rangeEnd = minYmd(effectiveEnd, projectionEnd);
      if (rangeStart > rangeEnd) continue;
      const days = getLessonDaysOfWeek(lesson);
      if (days.length === 0) continue;
      for (const day of days) {
        if (weekdayOccursInYmdRange(day, rangeStart, rangeEnd)) {
          out.push({ dayOfWeek: day, startTime: st, endTime: et });
        }
      }
      continue;
    }

    if (lesson.lessonType === 'private') {
      if (!lesson.startDate) continue;
      const n = lesson.numberOfSessions != null && lesson.numberOfSessions > 0 ? lesson.numberOfSessions : 1;
      const dates = generatePrivateLessonOccurrenceDates(lesson.startDate, n, lesson.daysOfWeek);
      const activeWeekdays = new Set<number>();
      for (const dt of dates) {
        const y = formatYmdLocal(dt);
        if (y >= startFromDate && y <= projectionEnd) {
          if (lesson.cancelledFromDate && y >= lesson.cancelledFromDate) {
            continue;
          }
          activeWeekdays.add(dt.getDay());
        }
      }
      for (const day of activeWeekdays) {
        out.push({ dayOfWeek: day, startTime: st, endTime: et });
      }
    }
  }

  return out;
}

/**
 * Whether this lesson has a session on the given calendar date (local YYYY-MM-DD).
 */
export function lessonHasSessionOnCalendarDate(
  lesson: Lesson,
  termsById: Map<string, Pick<Term, 'startDate' | 'endDate'>>,
  dateStr: string
): boolean {
  if (lesson.cancelledFromDate && dateStr >= lesson.cancelledFromDate) {
    return false;
  }

  const dow = calendarDayOfWeekFromYmd(dateStr);

  if (lesson.lessonType === 'group') {
    if (!lesson.termId) return false;
    const term = termsById.get(lesson.termId);
    if (!term) return false;
    const tStart = toYmd(term.startDate);
    const tEnd = toYmd(term.endDate);
    if (!tStart || !tEnd || dateStr < tStart || dateStr > tEnd) return false;
    const days = getLessonDaysOfWeek(lesson);
    if (!days.includes(dow)) return false;
    return true;
  }

  if (lesson.lessonType === 'camp') {
    const cStart = toYmd(lesson.startDate);
    const cEnd = toYmd(lesson.endDate);
    if (!cStart || !cEnd || dateStr < cStart || dateStr > cEnd) return false;
    const days = getLessonDaysOfWeek(lesson);
    return days.includes(dow);
  }

  if (lesson.lessonType === 'private') {
    if (lesson.termId) {
      const term = termsById.get(lesson.termId);
      if (!term) return false;
      const tStart = toYmd(term.startDate);
      const tEnd = toYmd(term.endDate);
      if (!tStart || !tEnd || dateStr < tStart || dateStr > tEnd) return false;
      const days = getLessonDaysOfWeek(lesson);
      return days.includes(dow);
    }
    if (!lesson.startDate) return false;
    const n = lesson.numberOfSessions != null && lesson.numberOfSessions > 0 ? lesson.numberOfSessions : 1;
    const dates = generatePrivateLessonOccurrenceDates(lesson.startDate, n, lesson.daysOfWeek);
    for (const dt of dates) {
      if (formatYmdLocal(dt) === dateStr) {
        return true;
      }
    }
    return false;
  }

  return false;
}

/**
 * True if the coach has another lesson overlapping [startTime, endTime] on dateStr.
 */
export function coachHasOverlappingLessonAt(
  lessons: Lesson[],
  termsById: Map<string, Pick<Term, 'startDate' | 'endDate'>>,
  dateStr: string,
  startTime: string,
  endTime: string
): boolean {
  const s = normalizeTimeHHMM(startTime);
  const e = normalizeTimeHHMM(endTime);
  for (const lesson of lessons) {
    if (!lessonHasSessionOnCalendarDate(lesson, termsById, dateStr)) continue;
    if (doTimesOverlap(normalizeTimeHHMM(lesson.startTime), normalizeTimeHHMM(lesson.endTime), s, e)) {
      return true;
    }
  }
  return false;
}
